import {
  CONTRACT_OCCURRENCE_STATUSES,
  CONTRACT_SORTS,
  CONTRACT_VIEWS,
  type ContractSort,
  type ContractView,
} from '@sahab/contracts'
import { z } from 'zod'

export const CONTRACT_PAGE_SIZES = [25, 50, 100] as const

const uuid = z.uuid().optional().catch(undefined)
const page = z.number().int().min(1).optional().catch(undefined)
const pageSize = z
  .number()
  .refine((n): n is (typeof CONTRACT_PAGE_SIZES)[number] =>
    (CONTRACT_PAGE_SIZES as readonly number[]).includes(n),
  )
  .optional()
  .catch(undefined)

export function isContractSort(value: string | undefined): value is ContractSort {
  return (CONTRACT_SORTS as readonly (string | undefined)[]).includes(value)
}

/**
 * The contracts register (`/service/contracts`) lives in the URL: the view tab, the search text, the
 * customer filter, sort and page. The default view (active) and sort (newest number first) never show.
 */
export const contractsSearchSchema = z.object({
  view: z.enum(CONTRACT_VIEWS).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  customerId: uuid,
  sort: z.string().refine(isContractSort).optional().catch(undefined),
  page,
  pageSize,
})
export type ContractsSearch = z.infer<typeof contractsSearchSchema>

export const CONTRACT_DEFAULT_VIEW: ContractView = 'active'
export const CONTRACT_DEFAULT_SORT: ContractSort = '-number'

export const CONTRACT_TABS = [
  'coverage',
  'plans',
  'visits',
  'billing',
  'terms',
  'jobs',
  'files',
  'tasks',
  'activity',
] as const
export type ContractTab = (typeof CONTRACT_TABS)[number]

/**
 * The contract page: the open tab, and the covered-asset list's site filter, search and page (the
 * Coverage tab); the Visits tab's plan, site and status filters and page; the Jobs tab's origin filter.
 */
export const contractPageSearchSchema = z.object({
  tab: z.enum(CONTRACT_TABS).optional().catch(undefined),
  site: uuid,
  aq: z.coerce.string().trim().max(200).optional().catch(undefined),
  apage: page,
  vplan: uuid,
  vsite: uuid,
  vstatus: z.enum(CONTRACT_OCCURRENCE_STATUSES).optional().catch(undefined),
  vpage: page,
  origin: z.enum(['REQUEST', 'PLANNED']).optional().catch(undefined),
  jpage: page,
  /** C4: opens the Renew dialog on arrival (the insights' "Renew"). */
  renew: z.boolean().optional().catch(undefined),
})
export type ContractPageSearch = z.infer<typeof contractPageSearchSchema>

/** New contract: the customer it starts with (the customer profile's "New contract"). */
export const contractNewSearchSchema = z.object({
  customerId: uuid,
})
export type ContractNewSearch = z.infer<typeof contractNewSearchSchema>

/**
 * The billing queue (`/service/contracts/billing`, C4): READY instalments across contracts, by customer,
 * contract and search text, a page at a time; all in the URL so a link (the insights' "Billing ready")
 * opens it filtered.
 */
export const billingQueueSearchSchema = z.object({
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  customerId: uuid,
  contractId: uuid,
  page,
  pageSize,
})
export type BillingQueueSearch = z.infer<typeof billingQueueSearchSchema>
